import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import AdminLayout from '@/components/layout/AdminLayout'
import LoginPage from '@/pages/admin/LoginPage'
import DashboardPage from '@/pages/admin/DashboardPage'
import ContactsPage from '@/pages/admin/ContactsPage'
import SettingsPage from '@/pages/admin/SettingsPage'
import { InvoicesPage, NewInvoicePage, InvoiceDetailPage } from '@/pages/admin/invoices'

// Client pages (STORY-005 to STORY-010)
import { ClientsPage, ClientDetailPage, NewClientPage, EditClientPage } from '@/pages/admin/clients'

// Quotes pages (STORY-016 to STORY-018)
import QuotesPage from '@/pages/admin/quotes/QuotesPage'
import NewQuotePage from '@/pages/admin/quotes/NewQuotePage'

// Products pages
import { ProductsPage } from '@/pages/admin/products'

// Public client pages (STORY-019, STORY-020)
import QuoteViewPage from '@/pages/client/QuoteViewPage'

// Projects pages (STORY-021 to STORY-024)
import { ProjectsPage, ProjectDetailPage, NewProjectPage, NewProjectPageWithAI, EditProjectPage } from '@/pages/admin/projects'

// Notifications page (STORY-033)
import { NotificationsPage } from '@/pages/admin/notifications'

// Reminders pages (STORY-030 to STORY-033)
import { RemindersPage } from '@/pages/admin/reminders'

// Email pages (STORY-025 to STORY-029)
import { EmailTemplatesPage, EmailLogsPage } from '@/pages/admin/emails'
import { EmailSettingsPage } from '@/pages/admin/settings'

// AI pages (STORY-034 to STORY-038)
import { AIConversationsPage, AIQuestionnairePage } from '@/pages/admin/ai'
import { InterventionsPage, InterventionDetailPage } from '@/pages/admin/interventions'

// Tickets pages
import TicketsPage from '@/pages/admin/tickets/TicketsPage'
import TicketDetailPage from '@/pages/admin/tickets/TicketDetailPage'

// Assistance pages
import { AssistanceRequestsPage } from '@/pages/admin/assistance'

// RGPD pages (STORY-046 to STORY-048)
import { RGPDPage, DataRequestsPage } from '@/pages/admin/rgpd'

// Marketing pages (STORY-049 to STORY-051)
import { MarketingPage, NewsletterPage } from '@/pages/admin/marketing'
import UnsubscribePage from '@/pages/client/UnsubscribePage'
import UnsubscribeProspectionPage from '@/pages/client/UnsubscribeProspectionPage'

// Comptabilite pages (STORY-039 to STORY-042)
import { ComptabilitePage, RecurringPaymentsPage } from '@/pages/admin/comptabilite'

// Prospection pages
import { ProspectionPage, ProspectionDetailPage } from '@/pages/admin/prospection'

// Agenda pages (STORY-043 to STORY-045)
import AgendaPage from '@/pages/admin/agenda/AgendaPage'
import BookingPage from '@/pages/client/BookingPage'
import AppointmentConfirmPage from '@/pages/client/AppointmentConfirmPage'
import NotFoundPage from '@/pages/NotFoundPage'

interface ProtectedRouteProps {
  children: React.ReactNode
}

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, isAdmin } = useAuth()
  const [showRetry, setShowRetry] = useState(false)

  useEffect(() => {
    if (!loading) {
      setShowRetry(false)
      return
    }
    const timer = setTimeout(() => setShowRetry(true), 5000)
    return () => clearTimeout(timer)
  }, [loading])

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-cream-50 gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-mint-600"></div>
        {showRetry && (
          <div className="text-center">
            <p className="text-gray-500 text-sm mb-2">Le chargement prend plus de temps que prévu...</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-mint-600 text-white rounded-lg hover:bg-mint-700 transition-colors text-sm"
            >
              Recharger la page
            </button>
          </div>
        )}
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream-50">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Accès refusé</h1>
          <p className="text-gray-600 mb-4">Vous n'avez pas les permissions nécessaires pour accéder à l'administration.</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-mint-600 text-white rounded-lg hover:bg-mint-700 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default function AppRouter() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />

      {/* Public client quote view (STORY-019) */}
      <Route path="/devis/view/:token" element={<QuoteViewPage />} />

      {/* Public unsubscribe page (STORY-051) */}
      <Route path="/unsubscribe/:token" element={<UnsubscribePage />} />

      {/* Prospection unsubscribe (campaigns) */}
      <Route
        path="/unsubscribe/prospection/:token"
        element={<UnsubscribeProspectionPage />}
      />

      {/* Public booking page (STORY-044) */}
      <Route path="/booking/:adminId" element={<BookingPage />} />

      {/* Public appointment confirmation (STORY-045) */}
      <Route path="/rdv/confirm/:token" element={<AppointmentConfirmPage />} />
      <Route path="/rdv/decline/:token" element={<AppointmentConfirmPage />} />

      {/* Protected admin routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="contacts" element={<ContactsPage />} />

        {/* Client routes (STORY-005 to STORY-010) */}
        <Route path="clients" element={<ClientsPage />} />
        <Route path="clients/new" element={<NewClientPage />} />
        <Route path="clients/:id" element={<ClientDetailPage />} />
        <Route path="clients/:id/edit" element={<EditClientPage />} />

        {/* Quotes routes (STORY-016 to STORY-018) */}
        <Route path="devis" element={<QuotesPage />} />
        <Route path="devis/new" element={<NewQuotePage />} />
        <Route path="devis/:id" element={<NewQuotePage />} />

        {/* Invoices routes */}
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/new" element={<NewInvoicePage />} />
        <Route path="invoices/:id" element={<InvoiceDetailPage />} />

        {/* Products routes */}
        <Route path="products" element={<ProductsPage />} />

        {/* Projects routes (STORY-021 to STORY-024) */}
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<NewProjectPage />} />
        <Route path="projects/new-ai" element={<NewProjectPageWithAI />} />
        <Route path="projects/:id" element={<ProjectDetailPage />} />
        <Route path="projects/:id/edit" element={<EditProjectPage />} />

        {/* Notifications route (STORY-033) */}
        <Route path="notifications" element={<NotificationsPage />} />

        {/* Reminders routes (STORY-030 to STORY-033) */}
        <Route path="reminders" element={<RemindersPage />} />

        {/* Email routes (STORY-025 to STORY-029) */}
        <Route path="emails/templates" element={<EmailTemplatesPage />} />
        <Route path="emails/logs" element={<EmailLogsPage />} />

        {/* AI routes (STORY-034 to STORY-038) */}
        <Route path="ai/conversations" element={<AIConversationsPage />} />
        <Route path="ai/questionnaire/:conversationId" element={<AIQuestionnairePage />} />
        <Route path="interventions" element={<InterventionsPage />} />
        <Route path="interventions/:id" element={<InterventionDetailPage />} />

        {/* Tickets routes */}
        <Route path="tickets" element={<TicketsPage />} />
        <Route path="tickets/:id" element={<TicketDetailPage />} />

        {/* Assistance routes */}
        <Route path="assistance" element={<AssistanceRequestsPage />} />

        {/* Settings routes */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/email" element={<EmailSettingsPage />} />

        {/* RGPD routes (STORY-046 to STORY-048) */}
        <Route path="rgpd" element={<RGPDPage />} />
        <Route path="rgpd/requests" element={<DataRequestsPage />} />

        {/* Marketing routes (STORY-049 to STORY-051) */}
        <Route path="marketing" element={<MarketingPage />} />
        <Route path="marketing/newsletter/:id" element={<NewsletterPage />} />

        {/* Comptabilite routes (STORY-039 to STORY-042) */}
        <Route path="comptabilite" element={<ComptabilitePage />} />
        <Route path="comptabilite/recurring" element={<RecurringPaymentsPage />} />

        {/* Prospection routes */}
        <Route path="prospection" element={<ProspectionPage />} />
        <Route path="prospection/:id" element={<ProspectionDetailPage />} />

        {/* Agenda routes (STORY-043) */}
        <Route path="agenda" element={<AgendaPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
